import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

export default function NewAccount() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    account_type: 'checking',
    asset_subtype: '',
    institution: '',
    principal_balance: '',
    income_balance: '',
    property_address: '',
    property_description: '',
    appraised_value: '',
    appraisal_date: ''
  })

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const isRealEstate = formData.account_type === 'real_estate'

  const handleSubmit = async () => {
    setError(null)

    if (!formData.name.trim()) {
      setError('Account name is required')
      return
    }

    setLoading(true)

    const principal = parseFloat(formData.principal_balance) || 0
    const income = parseFloat(formData.income_balance) || 0

    const { error: insertError } = await supabase
      .from('accounts')
      .insert({
        trust_id: id,
        name: formData.name.trim(),
        account_type: formData.account_type,
        asset_subtype: formData.asset_subtype || null,
        institution: isRealEstate ? null : (formData.institution || null),
        principal_balance: principal,
        income_balance: income,
        current_balance: principal + income,
        property_address: isRealEstate ? (formData.property_address || null) : null,
        property_description: isRealEstate ? (formData.property_description || null) : null,
        appraised_value: isRealEstate ? (parseFloat(formData.appraised_value) || null) : null,
        appraisal_date: isRealEstate ? (formData.appraisal_date || null) : null,
      })

    if (insertError) {
      setError(insertError.message)
      setLoading(false)
      return
    }

    // Log the action
    await supabase.from('audit_log').insert({
      trust_id: id,
      user_id: user.id,
      action: 'created_account',
      table_affected: 'accounts',
      new_value: formData
    })

    navigate(`/trusts/${id}?tab=accounts`)
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/trusts/${id}`)}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            ← Back
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Account</h2>
            <p className="text-sm text-gray-500">Add a financial or real estate account to this trust</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Account Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Fidelity Brokerage Account"
            />
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Type <span className="text-red-500">*</span>
            </label>
            <select
              name="account_type"
              value={formData.account_type}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
              <option value="investment">Investment</option>
              <option value="real_estate">Real Estate</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Asset Subtype */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subtype <span className="text-gray-400">(optional)</span>
            </label>
            {isRealEstate ? (
              <select
                name="asset_subtype"
                value={formData.asset_subtype}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select subtype...</option>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="agricultural">Agricultural</option>
                <option value="mineral_rights">Mineral Rights</option>
                <option value="other">Other</option>
              </select>
            ) : (
              <select
                name="asset_subtype"
                value={formData.asset_subtype}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select subtype...</option>
                <option value="brokerage">Brokerage</option>
                <option value="ira">IRA</option>
                <option value="401k">401(k)</option>
                <option value="money_market">Money Market</option>
                <option value="cd">Certificate of Deposit</option>
                <option value="other">Other</option>
              </select>
            )}
          </div>

          {/* Financial Account Fields */}
          {!isRealEstate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                name="institution"
                value={formData.institution}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Fidelity, Vanguard, Chase"
              />
            </div>
          )}

          {/* Real Estate Fields */}
          {isRealEstate && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Address
                </label>
                <input
                  type="text"
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123 Main St, Grand Rapids, MI 49503"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal Description
                </label>
                <textarea
                  name="property_description"
                  value={formData.property_description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Legal description of the property..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appraised Value
                  </label>
                  <input
                    type="number"
                    name="appraised_value"
                    value={formData.appraised_value}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appraisal Date
                  </label>
                  <input
                    type="date"
                    name="appraisal_date"
                    value={formData.appraisal_date}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Principal & Income Balances */}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3">Opening Balances</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Principal Balance
                </label>
                <input
                  type="number"
                  name="principal_balance"
                  value={formData.principal_balance}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Income Balance
                </label>
                <input
                  type="number"
                  name="income_balance"
                  value={formData.income_balance}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Principal and income are tracked separately per UPIA requirements
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding account...' : 'Add Account'}
            </button>
            <button
              onClick={() => navigate(`/trusts/${id}`)}
              className="px-6 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Layout>
  )
}